<template>
  <div class="site-category-page">
    <div class="page-header">
      <div class="header-left">
        <button class="back-btn" @click="$emit('back')">
          ← 목록으로
        </button>

        <div class="title-wrap">
          <div class="title">카테고리 설정</div>
          <div class="subtitle">
            {{ siteName || '-' }} 현장의 대분류 / 소분류 / 항목 할당을 관리합니다.
          </div>
        </div>
      </div>

      <div class="header-right">
        <button class="btn secondary" @click="fetchDetail">
          새로고침
        </button>
      </div>
    </div>

    <div class="category-layout">
      <!-- 대분류 -->
      <div class="category-col card">
        <div class="category-col-title">대분류</div>

        <div class="category-top-row">
          <input
            v-model="groupInputName"
            class="control"
            placeholder="대분류명"
            @keyup.enter="saveGroupInline"
          />

          <div class="sort-buttons">
            <button class="btn xs outline" :disabled="!selectedGroupId" @click="moveGroupUp">위</button>
            <button class="btn xs outline" :disabled="!selectedGroupId" @click="moveGroupDown">아래</button>
          </div>
        </div>

        <div class="category-actions">
          <button class="btn xs outline" @click="createGroupInline">추가</button>
          <button class="btn xs outline" :disabled="!selectedGroupId" @click="saveGroupInline">변경</button>
          <button class="btn xs outline danger" :disabled="!selectedGroupId" @click="deleteSelectedGroup">삭제</button>
        </div>

        <div class="category-box">
          <div v-if="groupList.length === 0" class="empty-text">
            등록된 대분류가 없습니다.
          </div>

          <div
            v-for="group in groupList"
            :key="group.id"
            class="category-item"
            :class="{ active: selectedGroupId === group.id }"
            @click="selectGroup(group)"
          >
            {{ group.name }}
          </div>
        </div>
      </div>

      <!-- 소분류 -->
      <div class="category-col card">
        <div class="category-col-title">소분류</div>

        <div class="category-top-row">
          <input
            v-model="subgroupInputName"
            class="control"
            placeholder="소분류명"
            :disabled="!selectedGroupId"
            @keyup.enter="saveSubgroupInline"
          />

          <div class="sort-buttons">
            <button class="btn xs outline" :disabled="!selectedSubgroupId" @click="moveSubgroupUp">위</button>
            <button class="btn xs outline" :disabled="!selectedSubgroupId" @click="moveSubgroupDown">아래</button>
          </div>
        </div>

        <div class="category-actions">
          <button class="btn xs outline" :disabled="!selectedGroupId" @click="createSubgroupInline">추가</button>
          <button class="btn xs outline" :disabled="!selectedSubgroupId" @click="saveSubgroupInline">변경</button>
          <button class="btn xs outline danger" :disabled="!selectedSubgroupId" @click="deleteSelectedSubgroup">삭제</button>
        </div>

        <div class="category-box">
          <div v-if="!selectedGroupId" class="empty-text">
            먼저 대분류를 선택해주세요.
          </div>

          <div v-else-if="selectedSubgroupList.length === 0" class="empty-text">
            등록된 소분류가 없습니다.
          </div>

          <div
            v-for="subgroup in selectedSubgroupList"
            v-else
            :key="subgroup.id"
            class="category-item"
            :class="{ active: selectedSubgroupId === subgroup.id }"
            @click="selectSubgroup(subgroup)"
          >
            {{ subgroup.name }}
          </div>
        </div>
      </div>

      <!-- 등록된 센서 -->
      <div class="category-col card">
        <div class="category-col-title">등록된 센서</div>

        <div class="category-top-row right-align">
          <div></div>

          <div class="sort-buttons">
            <button class="btn xs outline" :disabled="checkedAssignedItemKeys.length === 0" @click="moveAssignedItemUp">위</button>
            <button class="btn xs outline" :disabled="checkedAssignedItemKeys.length === 0" @click="moveAssignedItemDown">아래</button>
          </div>
        </div>

        <div class="category-actions single">
          <button
            class="btn xs outline"
            :disabled="checkedAssignedItemKeys.length === 0"
            @click="moveToUnassigned"
          >
            등록해제
          </button>
        </div>

        <div class="category-box">
          <div v-if="filteredAssignedItems.length === 0" class="empty-text">
            등록된 센서가 없습니다.
          </div>

          <label
            v-for="item in filteredAssignedItems"
            :key="item.itemKey"
            class="sensor-row"
            :class="{ active: checkedAssignedItemKeys.includes(item.itemKey) }"
          >
            <input
              type="checkbox"
              :value="item.itemKey"
              v-model="checkedAssignedItemKeys"
            />

            <div class="sensor-info">
              <div class="sensor-name-row">
                <span class="type-badge" :class="resolveItemTypeClass(item.itemType)">
                  {{ resolveItemTypeLabel(item.itemType) }}
                </span>
                <span class="sensor-name">{{ item.displayName || '-' }}</span>
              </div>

              <div class="sensor-desc">
                {{ item.displayCode || '-' }}
                <span v-if="item.loggerName"> / {{ item.loggerName }}</span>
                <span v-if="item.lineNumber"> / {{ item.lineNumber }}</span>
              </div>
            </div>
          </label>
        </div>
      </div>

      <!-- 등록 안된 센서 -->
      <div class="category-col card">
        <div class="category-col-title">등록 안된 센서</div>

        <div class="category-top-row empty-top"></div>

        <div class="category-actions single">
          <button
            class="btn xs outline"
            :disabled="!selectedGroupId || !selectedSubgroupId || checkedUnassignedItemKeys.length === 0"
            @click="moveToAssigned"
          >
            등록
          </button>
        </div>

        <div class="category-box">
          <div v-if="filteredUnassignedItems.length === 0" class="empty-text">
            등록 안된 센서가 없습니다.
          </div>

          <label
            v-for="item in filteredUnassignedItems"
            :key="item.itemKey"
            class="sensor-row"
            :class="{ active: checkedUnassignedItemKeys.includes(item.itemKey) }"
          >
            <input
              type="checkbox"
              :value="item.itemKey"
              v-model="checkedUnassignedItemKeys"
            />

            <div class="sensor-info">
              <div class="sensor-name-row">
                <span class="type-badge" :class="resolveItemTypeClass(item.itemType)">
                  {{ resolveItemTypeLabel(item.itemType) }}
                </span>
                <span class="sensor-name">{{ item.displayName || '-' }}</span>
              </div>

              <div class="sensor-desc">
                {{ item.displayCode || '-' }}
                <span v-if="item.loggerName"> / {{ item.loggerName }}</span>
                <span v-if="item.lineNumber"> / {{ item.lineNumber }}</span>
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/api/axios'

export default {
  name: 'SiteCategoryPage',

  props: {
    siteId: {
      type: Number,
      default: null
    },
    siteName: {
      type: String,
      default: ''
    }
  },

  emits: ['back'],

  data() {
    return {
      groupList: [],
      unassignedItems: [],

      selectedGroupId: null,
      selectedSubgroupId: null,

      assignedItemMap: {},

      checkedUnassignedItemKeys: [],
      checkedAssignedItemKeys: [],

      unassignedKeyword: '',
      assignedKeyword: '',

      groupInputName: '',
      subgroupInputName: ''
    }
  },

  computed: {
    selectedGroup() {
      return this.groupList.find(item => item.id === this.selectedGroupId) || null
    },

    selectedGroupName() {
      return this.selectedGroup?.name || ''
    },

    selectedSubgroupList() {
      return Array.isArray(this.selectedGroup?.subgroups)
        ? this.selectedGroup.subgroups
        : []
    },

    selectedSubgroup() {
      return this.selectedSubgroupList.find(item => item.id === this.selectedSubgroupId) || null
    },

    selectedSubgroupName() {
      return this.selectedSubgroup?.name || ''
    },

    assignedItems() {
      if (!this.selectedSubgroupId) return []
      return this.assignedItemMap[this.selectedSubgroupId] || []
    },

    filteredUnassignedItems() {
      return this.filterItems(this.unassignedItems, this.unassignedKeyword)
    },

    filteredAssignedItems() {
      return this.filterItems(this.assignedItems, this.assignedKeyword)
    }
  },

  watch: {
    siteId: {
      immediate: true,
      handler() {
        if (this.siteId) {
          this.fetchDetail()
        }
      }
    }
  },

  methods: {
    buildItemKey(item) {
      if (item.itemType === 'GROUP') {
        return `GROUP:${item.sensorGroupId}`
      }
      return `SENSOR:${item.sensorId}`
    },

    resolveItemTypeLabel(itemType) {
      if (itemType === 'GROUP') return '다단센서'
      return '단일센서'
    },

    resolveItemTypeClass(itemType) {
      if (itemType === 'GROUP') return 'group'
      return 'sensor'
    },

    normalizeSensorItem(sensor) {
      const sensorId = Number(sensor.sensorId || sensor.id || 0)

      return {
        itemType: 'SENSOR',
        itemKey: `SENSOR:${sensorId}`,

        sensorId,
        sensorGroupId: null,

        displayName: sensor.sensorName || sensor.name || '-',
        displayCode: sensor.sensorCode || sensor.code || '-',
        loggerName: sensor.loggerName || '',
        lineNumber: sensor.lineNumber || null,

        raw: sensor
      }
    },

    normalizeGroupItem(group) {
      const sensorGroupId = Number(group.sensorGroupId ?? group.id)

      const multiSensorType =
        group.multiSensorType ||
        group.multi_sensor_type ||
        group.raw?.multiSensorType ||
        group.raw?.multi_sensor_type ||
        null

      let displayCode = ''

      if (multiSensorType === 'TS') {
        displayCode = '지중경사계'
      } else if (multiSensorType === 'TN') {
        displayCode = '내공변위계'
      } else if (multiSensorType === 'R') {
        displayCode = '레일변위계'
      } else {
        displayCode = '다단센서'
      }

      return {
        itemType: 'GROUP',
        itemKey: `GROUP:${sensorGroupId}`,

        sensorId: null,
        sensorGroupId,

        displayName: group.groupName || group.name || '-',
        displayCode,
        loggerName: group.sensorLoggerName || group.loggerName || '',
        lineNumber: group.sensorLoggerLineNumber || group.lineNumber || null,

        raw: group
      }
    },

    normalizeMixedItems(list) {
      const safeList = Array.isArray(list) ? list : []

      return safeList.map(item => {
        if (item.itemType === 'GROUP') {
          return this.normalizeGroupItem(item)
        }
        return this.normalizeSensorItem(item)
      })
    },

    async fetchDetail() {
      if (!this.siteId) return

      const keepGroupId = this.selectedGroupId
      const keepSubgroupId = this.selectedSubgroupId

      try {
        const res = await api.get('/app/siteCategory/detail', {
          params: {
            siteId: this.siteId
          }
        })

        const result = res?.data?.result || {}

        const groups = Array.isArray(result.groups) ? result.groups : []
        const unassignedSensors = Array.isArray(result.unassignedSensors)
          ? result.unassignedSensors
          : []

        this.groupList = groups.map(group => ({
          id: group.id,
          name: group.name,
          sortOrder: group.sortOrder ?? 0,
          subgroups: Array.isArray(group.subgroups)
            ? group.subgroups.map(sub => {
                const rawItems = Array.isArray(sub.sensors)
                  ? sub.sensors
                  : []

                return {
                  id: sub.id,
                  name: sub.name,
                  sortOrder: sub.sortOrder ?? 0,
                  items: this.normalizeMixedItems(rawItems)
                }
              })
            : []
        }))

        this.unassignedItems = this.normalizeMixedItems(unassignedSensors)

        const nextAssignedItemMap = {}

        this.groupList.forEach(group => {
          group.subgroups.forEach(sub => {
            nextAssignedItemMap[sub.id] = Array.isArray(sub.items)
              ? [...sub.items]
              : []
          })
        })

        this.assignedItemMap = nextAssignedItemMap

        const restoredGroup = this.groupList.find(group => group.id === keepGroupId)

        if (restoredGroup) {
          this.selectedGroupId = restoredGroup.id

          const restoredSubgroup = restoredGroup.subgroups.find(sub => sub.id === keepSubgroupId)
          this.selectedSubgroupId = restoredSubgroup
            ? restoredSubgroup.id
            : (restoredGroup.subgroups?.[0]?.id || null)
        } else if (this.groupList.length > 0) {
          const firstGroup = this.groupList[0]
          this.selectedGroupId = firstGroup.id
          this.selectedSubgroupId = firstGroup.subgroups?.[0]?.id || null
        } else {
          this.selectedGroupId = null
          this.selectedSubgroupId = null
        }

        this.groupInputName = this.selectedGroup?.name || ''
        this.subgroupInputName = this.selectedSubgroup?.name || ''

        this.checkedUnassignedItemKeys = []
        this.checkedAssignedItemKeys = []
      } catch (e) {
        console.error('fetch site category detail error', e)
        this.groupList = []
        this.unassignedItems = []
        this.assignedItemMap = {}
        this.selectedGroupId = null
        this.selectedSubgroupId = null
        this.groupInputName = ''
        this.subgroupInputName = ''
      }
    },

    selectGroup(group) {
      this.selectedGroupId = group.id

      const subgroupList = Array.isArray(group.subgroups) ? group.subgroups : []
      this.selectedSubgroupId = subgroupList.length > 0 ? subgroupList[0].id : null

      this.groupInputName = group.name || ''
      this.subgroupInputName = this.selectedSubgroup?.name || ''

      this.checkedUnassignedItemKeys = []
      this.checkedAssignedItemKeys = []
    },

    selectSubgroup(subgroup) {
      this.selectedSubgroupId = subgroup.id
      this.subgroupInputName = subgroup.name || ''

      this.checkedUnassignedItemKeys = []
      this.checkedAssignedItemKeys = []
    },

    async createGroupInline() {
      const name = String(this.groupInputName || '').trim()
      if (!name) {
        alert('대분류명을 입력해주세요.')
        return
      }

      try {
        const nextSortOrder = this.groupList.length + 1

        await api.post('/app/siteCategory/group/save', {
          siteId: this.siteId,
          name,
          sortOrder: nextSortOrder
        })

        await this.fetchDetail()
      } catch (e) {
        console.error('create group error', e)
        alert('대분류 추가에 실패했습니다.')
      }
    },

    async saveGroupInline() {
      if (!this.selectedGroupId) {
        alert('변경할 대분류를 선택해주세요.')
        return
      }

      const name = String(this.groupInputName || '').trim()
      if (!name) {
        alert('대분류명을 입력해주세요.')
        return
      }

      try {
        await api.post('/app/siteCategory/group/update', {
          id: this.selectedGroupId,
          siteId: this.siteId,
          name,
          sortOrder: this.selectedGroup?.sortOrder ?? 0
        })

        await this.fetchDetail()
      } catch (e) {
        console.error('save group error', e)
        alert('대분류 변경에 실패했습니다.')
      }
    },

    async deleteSelectedGroup() {
      if (!this.selectedGroup) return
      await this.deleteGroup(this.selectedGroup)
    },

    async deleteGroup(group) {
      if (!window.confirm(`대분류 "${group.name}" 을(를) 삭제하시겠습니까?`)) return

      try {
        await api.post('/app/siteCategory/group/delete', {
          id: group.id
        })

        await this.fetchDetail()
      } catch (e) {
        console.error('delete group error', e)
        alert('대분류 삭제에 실패했습니다.')
      }
    },

    async createSubgroupInline() {
      if (!this.selectedGroupId) {
        alert('대분류를 먼저 선택해주세요.')
        return
      }

      const name = String(this.subgroupInputName || '').trim()
      if (!name) {
        alert('소분류명을 입력해주세요.')
        return
      }

      try {
        const nextSortOrder = this.selectedSubgroupList.length + 1

        await api.post('/app/siteCategory/subgroup/save', {
          siteSensorGroupId: this.selectedGroupId,
          name,
          sortOrder: nextSortOrder
        })

        await this.fetchDetail()
      } catch (e) {
        console.error('create subgroup error', e)
        alert('소분류 추가에 실패했습니다.')
      }
    },

    async saveSubgroupInline() {
      if (!this.selectedSubgroupId) {
        alert('변경할 소분류를 선택해주세요.')
        return
      }

      const name = String(this.subgroupInputName || '').trim()
      if (!name) {
        alert('소분류명을 입력해주세요.')
        return
      }

      try {
        await api.post('/app/siteCategory/subgroup/update', {
          id: this.selectedSubgroupId,
          siteSensorGroupId: this.selectedGroupId,
          name,
          sortOrder: this.selectedSubgroup?.sortOrder ?? 0
        })

        await this.fetchDetail()
      } catch (e) {
        console.error('save subgroup error', e)
        alert('소분류 변경에 실패했습니다.')
      }
    },

    async deleteSelectedSubgroup() {
      if (!this.selectedSubgroup) return
      await this.deleteSubgroup(this.selectedSubgroup)
    },

    async deleteSubgroup(subgroup) {
      if (!window.confirm(`소분류 "${subgroup.name}" 을(를) 삭제하시겠습니까?`)) return

      try {
        await api.post('/app/siteCategory/subgroup/delete', {
          id: subgroup.id
        })

        await this.fetchDetail()
      } catch (e) {
        console.error('delete subgroup error', e)
        alert('소분류 삭제에 실패했습니다.')
      }
    },

    async moveGroupUp() {
      await this.moveGroup(-1)
    },

    async moveGroupDown() {
      await this.moveGroup(1)
    },

    async moveGroup(direction) {
      const index = this.groupList.findIndex(group => group.id === this.selectedGroupId)
      const targetIndex = index + direction

      if (index < 0 || targetIndex < 0 || targetIndex >= this.groupList.length) {
        return
      }

      const current = this.groupList[index]
      const target = this.groupList[targetIndex]

      const currentSortOrder = current.sortOrder ?? index + 1
      const targetSortOrder = target.sortOrder ?? targetIndex + 1

      try {
        await api.post('/app/siteCategory/group/update', {
          id: current.id,
          siteId: this.siteId,
          name: current.name,
          sortOrder: targetSortOrder
        })

        await api.post('/app/siteCategory/group/update', {
          id: target.id,
          siteId: this.siteId,
          name: target.name,
          sortOrder: currentSortOrder
        })

        await this.fetchDetail()
      } catch (e) {
        console.error('move group error', e)
        alert('대분류 순서 변경에 실패했습니다.')
      }
    },

    async moveSubgroupUp() {
      await this.moveSubgroup(-1)
    },

    async moveSubgroupDown() {
      await this.moveSubgroup(1)
    },

    async moveSubgroup(direction) {
      const list = this.selectedSubgroupList
      const index = list.findIndex(sub => sub.id === this.selectedSubgroupId)
      const targetIndex = index + direction

      if (index < 0 || targetIndex < 0 || targetIndex >= list.length) {
        return
      }

      const current = list[index]
      const target = list[targetIndex]

      const currentSortOrder = current.sortOrder ?? index + 1
      const targetSortOrder = target.sortOrder ?? targetIndex + 1

      try {
        await api.post('/app/siteCategory/subgroup/update', {
          id: current.id,
          siteSensorGroupId: this.selectedGroupId,
          name: current.name,
          sortOrder: targetSortOrder
        })

        await api.post('/app/siteCategory/subgroup/update', {
          id: target.id,
          siteSensorGroupId: this.selectedGroupId,
          name: target.name,
          sortOrder: currentSortOrder
        })

        await this.fetchDetail()
      } catch (e) {
        console.error('move subgroup error', e)
        alert('소분류 순서 변경에 실패했습니다.')
      }
    },

    async moveToAssigned() {
      if (!this.selectedSubgroupId) {
        alert('소분류를 선택해주세요.')
        return
      }

      const moveKeySet = new Set(this.checkedUnassignedItemKeys)

      const movingItems = this.unassignedItems.filter(item =>
        moveKeySet.has(item.itemKey)
      )

      const remainUnassigned = this.unassignedItems.filter(item =>
        !moveKeySet.has(item.itemKey)
      )

      const currentAssigned = this.assignedItemMap[this.selectedSubgroupId] || []
      const currentAssignedKeySet = new Set(currentAssigned.map(item => item.itemKey))

      const nextAssigned = [
        ...currentAssigned,
        ...movingItems.filter(item => !currentAssignedKeySet.has(item.itemKey))
      ]

      this.assignedItemMap = {
        ...this.assignedItemMap,
        [this.selectedSubgroupId]: nextAssigned
      }

      this.unassignedItems = remainUnassigned
      this.checkedUnassignedItemKeys = []

      await this.saveAssignments(false)
    },

    async moveToUnassigned() {
      if (!this.selectedSubgroupId) return

      const moveKeySet = new Set(this.checkedAssignedItemKeys)
      const currentAssigned = this.assignedItemMap[this.selectedSubgroupId] || []

      const movingItems = currentAssigned.filter(item =>
        moveKeySet.has(item.itemKey)
      )

      const remainAssigned = currentAssigned.filter(item =>
        !moveKeySet.has(item.itemKey)
      )

      const unassignedKeySet = new Set(this.unassignedItems.map(item => item.itemKey))

      this.assignedItemMap = {
        ...this.assignedItemMap,
        [this.selectedSubgroupId]: remainAssigned
      }

      this.unassignedItems = [
        ...this.unassignedItems,
        ...movingItems.filter(item => !unassignedKeySet.has(item.itemKey))
      ]

      this.checkedAssignedItemKeys = []

      await this.saveAssignments(false)
    },

    async moveAssignedItemUp() {
      await this.moveAssignedItem(-1)
    },

    async moveAssignedItemDown() {
      await this.moveAssignedItem(1)
    },

    async moveAssignedItem(direction) {
      if (!this.selectedSubgroupId) return

      const selectedKey = this.checkedAssignedItemKeys[0]
      if (!selectedKey) return

      const list = [...(this.assignedItemMap[this.selectedSubgroupId] || [])]
      const index = list.findIndex(item => item.itemKey === selectedKey)
      const targetIndex = index + direction

      if (index < 0 || targetIndex < 0 || targetIndex >= list.length) {
        return
      }

      const temp = list[index]
      list[index] = list[targetIndex]
      list[targetIndex] = temp

      this.assignedItemMap = {
        ...this.assignedItemMap,
        [this.selectedSubgroupId]: list
      }

      this.checkedAssignedItemKeys = [selectedKey]

      await this.saveAssignments(false)
    },

    async saveAssignments(showAlert = true) {
      if (!this.selectedGroupId || !this.selectedSubgroupId) {
        if (showAlert) {
          alert('대분류 / 소분류를 선택해주세요.')
        }
        return
      }

      const items = this.assignedItems || []

      const sensorIds = []
      const sensorGroupIds = []

      items.forEach(item => {
        if (item.itemType === 'GROUP') {
          if (item.sensorGroupId) {
            sensorGroupIds.push(item.sensorGroupId)
          }
        } else {
          if (item.sensorId) {
            sensorIds.push(item.sensorId)
          }
        }
      })

      try {
        await api.post('/app/siteCategory/assignment/save', {
          siteId: this.siteId,
          groupId: this.selectedGroupId,
          subgroupId: this.selectedSubgroupId,
          sensorIds,
          sensorGroupIds
        })

        if (showAlert) {
          alert('항목 할당이 저장되었습니다.')
        }

        await this.fetchDetail()
      } catch (e) {
        console.error('saveAssignments error', e)
        alert('저장 실패')
        await this.fetchDetail()
      }
    },

    filterItems(list, keyword) {
      const safeList = Array.isArray(list) ? list : []
      const q = String(keyword || '').trim().toLowerCase()
      if (!q) return safeList

      return safeList.filter(item => {
        const target = [
          item.displayName,
          item.displayCode,
          item.loggerName,
          item.lineNumber
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return target.includes(q)
      })
    }
  }
}
</script>

<style scoped>
.site-category-page {
  width: 100%;
  min-height: 100%;
  padding: 18px 18px 26px;
  box-sizing: border-box;
  background: #f6f8fb;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.header-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.back-btn {
  border: none;
  background: #ffffff;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.subtitle {
  font-size: 13px;
  color: #64748b;
}

.category-layout {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 14px;
  min-height: 640px;
}

.card {
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  box-sizing: border-box;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
  min-height: 0;
}

.category-col {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.category-col-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  text-align: center;
  padding-bottom: 14px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 14px;
}

.category-top-row {
  min-height: 44px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
}

.category-top-row.empty-top {
  display: block;
}

.sort-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.category-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.category-actions.single {
  grid-template-columns: 1fr;
}

.category-box {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid #dce3ee;
  border-radius: 10px;
  background: #ffffff;
  padding: 10px;
}

.category-item {
  padding: 12px 10px;
  border-bottom: 1px solid #edf2f7;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  cursor: pointer;
  border-radius: 8px;
}

.category-item:last-child {
  border-bottom: none;
}

.category-item:hover {
  background: #f8fafc;
}

.category-item.active {
  background: #eef5ff;
  box-shadow: inset 3px 0 0 #3b82f6;
}

.control {
  width: 100%;
  height: 38px;
  border: 1px solid #d7deea;
  border-radius: 8px;
  padding: 0 10px;
  box-sizing: border-box;
  background: #fff;
  font-size: 14px;
  color: #111827;
}

.control:disabled {
  background: #f1f5f9;
  color: #94a3b8;
}

.sensor-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 8px;
  border-bottom: 1px solid #edf2f7;
  cursor: pointer;
  border-radius: 8px;
}

.sensor-row:last-child {
  border-bottom: none;
}

.sensor-row:hover {
  background: #f8fafc;
}

.sensor-row.active {
  background: #eef5ff;
}

.sensor-info {
  min-width: 0;
  flex: 1;
}

.sensor-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.sensor-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
}

.sensor-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  word-break: break-word;
}

.type-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.type-badge.sensor {
  background: #e7f0ff;
  color: #1d4ed8;
}

.type-badge.group {
  background: #f3e8ff;
  color: #7c3aed;
}

.empty-text {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.btn.primary {
  background: #f59e0b;
  color: #fff;
}

.btn.secondary {
  background: #e2e8f0;
  color: #334155;
}

.btn.outline {
  background: #ffffff;
  border: 1px solid #3b82f6;
  color: #2563eb;
}

.btn.outline:hover:not(:disabled) {
  background: #eff6ff;
}

.btn.outline.danger {
  border-color: #fca5a5;
  color: #ef4444;
}

.btn.outline.danger:hover:not(:disabled) {
  background: #fff1f2;
}

.btn.xs {
  padding: 7px 10px;
  font-size: 12px;
}

.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 1400px) {
  .category-layout {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 900px) {
  .category-layout {
    grid-template-columns: 1fr;
  }
}
</style>